/**
 * Campaign import/export API methods: PSA, external, orders, cert entry, price review
 */

import type {
  CertLookupResult,
  CertImportResult,
  OrdersImportResult, OrdersConfirmItem, BulkSaleResult,
  ScanCertResponse, ScanCertsResponse, ResolveCertResponse,
} from '../../types/campaigns';
import type { PriceFlagsResponse } from '../../types/campaigns/priceReview';
import type { PSAPendingItem } from '../../types/admin';
import { APIClient } from './client';

declare module './client' {
  interface APIClient {
    // Cert lookup
    lookupCert(certNumber: string): Promise<CertLookupResult>;

    // Orders sales import
    importOrdersSales(file: File): Promise<OrdersImportResult>;
    confirmOrdersSales(items: OrdersConfirmItem[]): Promise<BulkSaleResult>;

    // Cert entry
    importCerts(certNumbers: string[]): Promise<CertImportResult>;
    scanCert(certNumber: string): Promise<ScanCertResponse>;
    scanCerts(certNumbers: string[]): Promise<ScanCertsResponse>;
    resolveCert(certNumber: string): Promise<ResolveCertResponse>;

    // Price review & flags
    setReviewedPrice(purchaseId: string, priceCents: number, source: string, options?: { manualList?: boolean; priceOnly?: boolean }): Promise<{ success: boolean; reviewedAt: string }>;
    createPriceFlag(purchaseId: string, reason: string): Promise<{ id: number; flaggedAt: string }>;
    listPriceFlags(status?: string): Promise<PriceFlagsResponse>;
    resolvePriceFlag(flagId: number): Promise<void>;

    // PSA pending items
    listPSAPendingItems(): Promise<{ items: PSAPendingItem[] }>;
    assignPSAPendingItem(id: string, campaignId: string): Promise<unknown>;
    dismissPSAPendingItem(id: string): Promise<void>;
  }
}

const proto = APIClient.prototype;

// Cert lookup
proto.lookupCert = async function (this: APIClient, certNumber: string): Promise<CertLookupResult> {
  return this.get<CertLookupResult>(`/certs/${encodeURIComponent(certNumber)}`);
};

// Orders sales import
proto.importOrdersSales = async function (this: APIClient, file: File): Promise<OrdersImportResult> {
  return this.uploadFile<OrdersImportResult>('/purchases/import-orders', file);
};

proto.confirmOrdersSales = async function (this: APIClient, items: OrdersConfirmItem[]): Promise<BulkSaleResult> {
  return this.post<BulkSaleResult>('/purchases/import-orders/confirm', items);
};

// Cert entry
proto.importCerts = async function (this: APIClient, certNumbers: string[]): Promise<CertImportResult> {
  return this.post<CertImportResult>('/purchases/import-certs', { certNumbers });
};

proto.scanCert = async function (this: APIClient, certNumber: string): Promise<ScanCertResponse> {
  return this.post<ScanCertResponse>('/purchases/scan-cert', { certNumber });
};

proto.scanCerts = async function (this: APIClient, certNumbers: string[]): Promise<ScanCertsResponse> {
  return this.post<ScanCertsResponse>('/purchases/scan-certs', { certNumbers });
};

proto.resolveCert = async function (this: APIClient, certNumber: string): Promise<ResolveCertResponse> {
  return this.post<ResolveCertResponse>('/purchases/resolve-cert', { certNumber });
};

// Price review & flag endpoints
proto.setReviewedPrice = async function (
  this: APIClient, purchaseId: string, priceCents: number, source: string, options?: { manualList?: boolean; priceOnly?: boolean },
): Promise<{ success: boolean; reviewedAt: string }> {
  const response = await this.fetchWithRetry(
    `${this.baseURL}/purchases/${encodeURIComponent(purchaseId)}/review-price`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priceCents, source, ...(options?.manualList ? { manualList: true } : {}), ...(options?.priceOnly ? { priceOnly: true } : {}) }),
    }
  );
  return response.json() as Promise<{ success: boolean; reviewedAt: string }>;
};

proto.createPriceFlag = async function (
  this: APIClient, purchaseId: string, reason: string,
): Promise<{ id: number; flaggedAt: string }> {
  return this.post<{ id: number; flaggedAt: string }>(
    `/purchases/${encodeURIComponent(purchaseId)}/flag`,
    { reason },
  );
};

proto.listPriceFlags = async function (
  this: APIClient, status = 'open',
): Promise<PriceFlagsResponse> {
  return this.get<PriceFlagsResponse>(`/admin/price-flags?status=${encodeURIComponent(status)}`);
};

proto.resolvePriceFlag = async function (this: APIClient, flagId: number): Promise<void> {
  const response = await this.fetchWithRetry(
    `${this.baseURL}/admin/price-flags/${flagId}/resolve`,
    { method: 'PATCH' }
  );
  await this.expectNoContent(response);
};

// PSA pending items
proto.listPSAPendingItems = async function (this: APIClient): Promise<{ items: PSAPendingItem[] }> {
  return this.get<{ items: PSAPendingItem[] }>('/admin/psa-sync/pending');
};

proto.assignPSAPendingItem = async function (this: APIClient, id: string, campaignId: string): Promise<unknown> {
  return this.post(`/admin/psa-sync/pending/${encodeURIComponent(id)}/assign`, { campaignId });
};

proto.dismissPSAPendingItem = async function (this: APIClient, id: string): Promise<void> {
  await this.deleteResource(`/admin/psa-sync/pending/${encodeURIComponent(id)}`);
};
